import { K8sProviderIconStatus } from '@cast/features-cluster-ui';
import { ReactNode } from 'react';

import { Cluster } from '@cast/types';

import {
  isAnywhereCluster,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

import {
  InfoTooltip,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

const DisabledTooltip = ({
  clusterName,
  tooltip,
}: {
  clusterName?: string;
  tooltip?: ReactNode;
}) => (
  <InfoTooltip
    placement="bottom"
    iconSize="16px"
    title={
      <Stack gap={4}>
        <Typography variant="P12R">
          {clusterName}{' '}
          <Typography variant="P12B" component="span">
            Unavailable
          </Typography>
        </Typography>
        {tooltip && <Typography variant="P12R">{tooltip}</Typography>}
      </Stack>
    }
  />
);

const getCellState = ({
  cluster,
  anywhereDisabled,
  anywhereDisabledTooltip,
  kentManagedDisabled,
  kentManagedDisabledTooltip,
}: Props): { isDisabled: boolean; tooltip?: ReactNode } => {
  if (anywhereDisabled && isAnywhereCluster(cluster)) {
    return { isDisabled: true, tooltip: anywhereDisabledTooltip };
  }

  if (kentManagedDisabled && isKentManagedCluster(cluster)) {
    return { isDisabled: true, tooltip: kentManagedDisabledTooltip };
  }

  return { isDisabled: false };
};

type Props = {
  cluster: Cluster;
  anywhereDisabled?: boolean;
  anywhereDisabledTooltip?: ReactNode;
  kentManagedDisabled?: boolean;
  kentManagedDisabledTooltip?: ReactNode;
};

export const ClusterCell = ({
  cluster,
  anywhereDisabled,
  anywhereDisabledTooltip,
  kentManagedDisabled,
  kentManagedDisabledTooltip,
}: Props) => {
  const state = getCellState({
    cluster,
    anywhereDisabled,
    anywhereDisabledTooltip,
    kentManagedDisabled,
    kentManagedDisabledTooltip,
  });

  return (
    <Stack direction="row" gap={4} width="100%">
      <Box mt={3}>
        <K8sProviderIconStatus cluster={cluster} size={12} />
      </Box>
      <Stack flexGrow={1} minWidth={0} justifyContent="center">
        <Stack direction="row" gap={4} alignItems="center">
          <LinkBehaviour
            variant="P12M"
            noWrap
            data-testid="cluster-name"
            sx={{
              ...(state.isDisabled && {
                color: tokens.theme.fg.structural.secondary,
              }),
            }}
          >
            {cluster?.name}
          </LinkBehaviour>
          {state.isDisabled && (
            <DisabledTooltip
              clusterName={cluster?.name}
              tooltip={state.tooltip}
            />
          )}
        </Stack>
        {cluster?.providerNamespaceId && (
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
            noWrap
            mt={-4}
          >
            {cluster?.providerNamespaceId}
          </Typography>
        )}{' '}
      </Stack>
    </Stack>
  );
};
